/* AT2 EXPERIMENT - Comparison Modal - V2 */

/**
 * 🔶 Step 7: Modal de comparaison Original vs Optimisé
 *
 * Affiche:
 * - En haut: Résultat de l'analyse (score, éléments manquants)
 * - Gauche: Message original
 * - Droite: Loader (pendant génération) → Message optimisé (après, ÉDITABLE)
 * - Bas: Actions utilisateur (Envoyer optimisé / Envoyer original / Fermer)
 */

import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Edit as EditIcon, Close } from '@evaneos/b2b-ui/icons';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    paletteColors,
    TextField,
    Typography,
} from '@evaneos/b2b-ui';

import { AT2Analysis } from '../mock/at2-mock-api';
import type { AT2Mode } from '../hook/useAT2MessageCheck';

import { AT2AnalysisLoader } from './shared/AT2AnalysisLoader';

type Props = {
    open: boolean;
    originalMessage: string;
    improvedMessage: string | null;
    missingElements: AT2Analysis['missingElements'] | null;
    generating: boolean;
    mode: AT2Mode;
    onSendImproved: (editedMessage: string) => Promise<void>;
    onSendOriginal: () => Promise<void>;
    onClose: () => void;
};

export function AT2_ComparisonModal({
    open,
    originalMessage,
    improvedMessage,
    missingElements,
    generating,
    mode,
    onSendImproved,
    onSendOriginal,
    onClose,
}: Props) {
    const { formatMessage } = useIntl();
    const [editedMessage, setEditedMessage] = useState<string>(improvedMessage || '');
    const [isEditing, setIsEditing] = useState<boolean>(false);

    // Update editedMessage when improvedMessage changes
    useEffect(() => {
        if (improvedMessage) {
            setEditedMessage(improvedMessage);
            setIsEditing(false); // Reset editing mode when new message arrives
        }
    }, [improvedMessage]);

    const handleSendImproved = useCallback(() => {
        async function sendImproved() {
            await onSendImproved(editedMessage);
        }
        void sendImproved();
    }, [editedMessage, onSendImproved]);

    const handleSendOriginal = useCallback(() => {
        async function sendOriginal() {
            await onSendOriginal();
        }
        void sendOriginal();
    }, [onSendOriginal]);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    // Labels des boutons selon le mode
    const actionVerb =
        mode === 'schedule'
            ? formatMessage({ id: 'dossier.discussion.at2.modal.comparison.action.schedule' })
            : formatMessage({ id: 'dossier.discussion.at2.modal.comparison.action.send' });
    const improvedButtonLabel = formatMessage(
        { id: 'dossier.discussion.at2.modal.comparison.action.send_improved' },
        { action: actionVerb },
    );
    const originalButtonLabel = formatMessage(
        { id: 'dossier.discussion.at2.modal.comparison.action.send_original' },
        { action: actionVerb },
    );

    return (
        <Dialog open={open} maxWidth="lg">
            <DialogTitle>
                <Typography variant="h3">
                    {formatMessage({ id: 'dossier.discussion.at2.modal.comparison.title' })}
                </Typography>
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={() => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: 'black',
                })}
            >
                <Close />
            </IconButton>
            <DialogContent>
                {missingElements && (
                    <Box
                        component="ul"
                        display="flex"
                        flexDirection="column"
                        sx={{ listStyle: 'disc', pl: '13px' }}
                        mb={4}
                        mt={0}
                    >
                        {missingElements.map((element) => (
                            <li key={element}>
                                <Typography variant="body1">
                                    {element.split('.')[0] + '. '}
                                    <span style={{ fontWeight: 'bold' }}>
                                        {element.split('.')[1]}
                                    </span>
                                </Typography>
                            </li>
                        ))}
                    </Box>
                )}
                <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
                    <Box flex={1} color={generating ? 'grey.300' : 'inherit'}>
                        <Typography variant="body1" fontWeight="bold" mb={2} color="inherit">
                            {formatMessage({
                                id: 'dossier.discussion.at2.modal.comparison.your_version',
                            })}
                        </Typography>
                        <Box sx={{ p: 3, paddingLeft: 0, paddingTop: 0, fontSize: '14px' }}>
                            <Box
                                sx={{
                                    minHeight: '200px',
                                    maxHeight: '250px',
                                    overflowY: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: '20px',
                                    fontSize: '14px',
                                }}
                            >
                                {originalMessage}
                            </Box>
                        </Box>
                    </Box>
                    <Box flex={1} position="relative">
                        {!generating ? (
                            <Typography variant="body1" fontWeight="bold" mb={2}>
                                {formatMessage({
                                    id: 'dossier.discussion.at2.modal.comparison.improved_version',
                                })}
                            </Typography>
                        ) : null}
                        {!generating ? (
                            <Box
                                sx={{
                                    p: 3,
                                    fontSize: '14px',
                                    border: 1,
                                    borderColor: 'border.main',
                                    borderRadius: 2,
                                }}
                            >
                                {isEditing ? (
                                    <TextField
                                        fullWidth
                                        multiline
                                        autoFocus
                                        maxRows={15}
                                        value={editedMessage}
                                        onChange={(e) => setEditedMessage(e.target.value)}
                                        variant="standard"
                                        slotProps={{
                                            input: {
                                                disableUnderline: true,
                                                sx: {
                                                    whiteSpace: 'pre-wrap',
                                                    lineHeight: '20px',
                                                    fontSize: '14px',
                                                },
                                            },
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            minHeight: '200px',
                                            maxHeight: '250px',
                                            overflowY: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            lineHeight: '20px',
                                            fontSize: '14px',
                                        }}
                                    >
                                        {improvedMessage}
                                    </Box>
                                )}
                            </Box>
                        ) : null}
                        {generating ? (
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={2}
                                height="100%"
                                justifyContent="center"
                            >
                                <Typography variant="h4" color={paletteColors.green[900]}>
                                    {formatMessage({
                                        id: 'dossier.discussion.at2.modal.comparison.generating',
                                    })}
                                </Typography>
                                <AT2AnalysisLoader sx={{ width: '100%' }} />
                                <AT2AnalysisLoader sx={{ width: '80%' }} />
                                <AT2AnalysisLoader sx={{ width: '60%' }} />
                            </Box>
                        ) : null}
                        {!isEditing && !generating && (
                            <IconButton
                                onClick={handleEditClick}
                                sx={{
                                    position: 'absolute',
                                    bottom: 36,
                                    right: 48,
                                    bgcolor: 'white',
                                    border: 1,
                                    borderColor: 'grey.300',
                                    '&:hover': {
                                        bgcolor: 'grey.50',
                                    },
                                }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleSendOriginal} variant="outlined" disabled={generating}>
                    {originalButtonLabel}
                </Button>
                <Button
                    onClick={handleSendImproved}
                    variant="contained"
                    color="primary"
                    disabled={generating || !editedMessage}
                    loading={generating}
                >
                    {improvedButtonLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
